import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// Button
//
// Primary action button with KIBORG token overrides.
// Variants: default, secondary, destructive, outline, ghost
// Sizes: sm, default, lg
// Loading state with spinner
// Design: §6.1 Buttons
// ─────────────────────────────────────────────

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-[6px] rounded-control",
    "text-[14px] font-medium leading-none",
    "transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-page",
    "disabled:pointer-events-none disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "bg-accent text-white",
        ].join(" "),
        secondary: [
          "bg-hover text-text-secondary hover:bg-border-default",
        ].join(" "),
        destructive: [
          "bg-danger text-white hover:bg-danger/90",
        ].join(" "),
        outline: [
          "border border-border-default bg-transparent text-text-secondary hover:bg-hover",
        ].join(" "),
        ghost: [
          "bg-transparent text-text-secondary hover:bg-hover",
        ].join(" "),
        accent: [
          "bg-accent text-white hover:opacity-90",
        ].join(" "),
      },
      size: {
        sm: "h-7 px-3 text-[13px]",
        default: "h-9 px-4",
        lg: "h-10 px-6",
        icon: "h-9 w-9 p-0",
        "icon-sm": "h-7 w-7 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  leadingIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size,
      asChild = false,
      isLoading,
      leadingIcon,
      children,
      disabled,
      style,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    // The accent background for `default`/`accent` variants is applied via the
    // `bg-accent` class (see buttonVariants) so a caller-supplied `className`
    // (e.g. `bg-danger`) can override it. Using an inline style here would beat
    // every class and defeat those overrides.
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || isLoading}
        style={style}
        {...props}
      >
        {isLoading ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />
        ) : leadingIcon ? (
          leadingIcon
        ) : null}
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
